import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getActiveJobs } from '../services/jobService';
import { applyForJob } from '../services/applicationService';
import { getProfileStats } from '../services/seekerProfileService';
import JobCard from '../components/JobCard';
import JobApplicationModal from '../components/JobApplicationModal';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';

import ContentWithAds from '../components/ContentWithAds';
import { locationsByState } from '../constants/locations';

const Jobs = ({ user, userData }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJobApplicationModal, setShowJobApplicationModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [filters, setFilters] = useState({
    jobType: '',
    workMode: '',
    experienceLevel: '',
    state: '',
    city: '',
    gender: '',
    search: ''
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, filters]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      console.log('Fetching all active jobs...');
      const result = await getActiveJobs({}, { limit: 100 });
      console.log('Jobs result:', result);
      if (result.success) {
        setJobs(result.jobs);
      } else {
        console.error('Failed to fetch jobs:', result.error);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = jobs;

    // Filter by job type
    if (filters.jobType) {
      filtered = filtered.filter(job => job.jobType === filters.jobType);
    }

    // Filter by work mode
    if (filters.workMode) {
      filtered = filtered.filter(job => job.workMode === filters.workMode);
    }

    // Filter by experience level
    if (filters.experienceLevel) {
      filtered = filtered.filter(job => job.experienceLevel === filters.experienceLevel);
    }

    // Filter by location (State and City)
    if (filters.state) {
      filtered = filtered.filter(job =>
        job?.location?.state?.toLowerCase() === filters.state.toLowerCase()
      );
    }

    if (filters.city) {
      filtered = filtered.filter(job =>
        job?.location?.city?.toLowerCase() === filters.city.toLowerCase()
      );
    }

    // Filter by gender
    if (filters.gender) {
      filtered = filtered.filter(job => {
        if (filters.gender === 'male') return job.gender === 'male' || job.gender === 'both';
        if (filters.gender === 'female') return job.gender === 'female' || job.gender === 'both';
        return job.gender === filters.gender;
      });
    }

    // Filter by search term
    if (filters.search) {
      filtered = filtered.filter(job =>
        job.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        job.company.toLowerCase().includes(filters.search.toLowerCase()) ||
        job.description.toLowerCase().includes(filters.search.toLowerCase()) ||
        job.requiredSkills.some(skill =>
          skill.toLowerCase().includes(filters.search.toLowerCase())
        )
      );
    }

    setFilteredJobs(filtered);
  };

  const handleApplyClick = async (job, setJobApplying, setJobApplied) => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Check if profile is complete before showing modal
    try {
      const profileStats = await getProfileStats(user.uid);
      if (profileStats.success && profileStats.stats.completionPercentage < 85) {
        if (window.confirm('Your profile is not complete. You need at least 85% completion to apply for jobs. Would you like to complete your profile now?')) {
          navigate('/seeker-profile');
        }
        return;
      }
    } catch (error) {
      console.error('Error checking profile completion:', error);
    }

    // All checks passed, show the application modal
    setSelectedJob(job);
    setShowJobApplicationModal(true);
  };

  const handleConfirmApplication = async (applicationData) => {
    if (!selectedJob) return;

    setApplying(true);
    try {
      const result = await applyForJob(selectedJob.id, user.uid, applicationData);

      if (result.success) {
        setShowJobApplicationModal(false);
        setSelectedJob(null);
        setShowSuccessAnimation(true);

        // Hide success animation after 3 seconds
        setTimeout(() => {
          setShowSuccessAnimation(false);
        }, 3000);

        // Refresh jobs to update applied status
        fetchJobs();
      } else {
        alert(result.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error applying for job:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      jobType: '',
      workMode: '',
      experienceLevel: '',
      state: '',
      city: '',
      gender: '',
      search: ''
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="min-h-screen bg-slate-50/50 relative overflow-hidden pb-24 lg:pb-0">
        {/* Background grid pattern */}
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(#0e3a61_1px,transparent_1px)] [background-size:24px_24px] opacity-5"></div>

        {/* Hero Header Banner */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="bg-gradient-to-br from-[#0e3a61] to-[#051728] rounded-[2rem] rounded-tr-[4rem] rounded-bl-[4rem] shadow-xl border border-[#0e3a61]/10 overflow-hidden relative p-8 md:p-12">
            {/* Glowing background highlights */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-20 translate-x-20 pointer-events-none"></div>
            <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-[#e2822f]/10 rounded-full blur-2xl pointer-events-none"></div>
            
            <div className="relative text-center z-10">
              <span className="text-[10px] uppercase font-bold tracking-widest text-[#e2822f] bg-[#e2822f]/15 px-3 py-1 rounded-full border border-[#e2822f]/20">
                Executive Pipeline Explorer
              </span>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white mt-4 tracking-tight">
                Explore Global Opportunities
              </h1>
              <p className="text-sm md:text-base text-slate-300 mt-3 max-w-2xl mx-auto leading-relaxed">
                Analyze open requisitions, target premier pipelines, and query matches tailored to your expertise.
              </p>
              <div className="flex items-center justify-center gap-2 mt-6 text-slate-300">
                <div className="w-2 h-2 bg-[#508d0c] rounded-full animate-pulse"></div>
                <span className="text-xs font-bold uppercase tracking-wider">{jobs.length} Active Pipelines Online</span>
              </div>
            </div>
          </div>
        </div>

        <ContentWithAds>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Search and Filters Card */}
            <div className="bg-white rounded-[2rem] shadow-xl p-6 mb-8 border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-[#e2822f] to-[#d97706] rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h2 className="text-xs font-extrabold text-slate-900 uppercase tracking-widest">Opportunity Database Query</h2>
              </div>

              <div className="space-y-6">
                {/* Search Bar */}
                <div>
                  <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-widest mb-2">
                    Search Requisitions
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Query by title, industry domain, key competencies, or company parameters..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-xl leading-5 bg-slate-50/50 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0e3a61] text-sm"
                    />
                  </div>
                </div>

                {/* Filter Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">Job Type</label>
                    <select
                      value={filters.jobType}
                      onChange={(e) => handleFilterChange('jobType', e.target.value)}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0e3a61] bg-slate-50/50 text-sm font-semibold text-slate-700"
                    >
                      <option value="">All Job Types</option>
                      <option value="full-time">Full-time</option>
                      <option value="part-time">Part-time</option>
                      <option value="contract">Contract</option>
                      <option value="internship">Internship</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">Work Mode</label>
                    <select
                      value={filters.workMode}
                      onChange={(e) => handleFilterChange('workMode', e.target.value)}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0e3a61] bg-slate-50/50 text-sm font-semibold text-slate-700"
                    >
                      <option value="">All Work Modes</option>
                      <option value="Work From Home">Work From Home</option>
                      <option value="In Field">In Field</option>
                      <option value="Work From Office">Work From Office</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">Experience Level</label>
                    <select
                      value={filters.experienceLevel}
                      onChange={(e) => handleFilterChange('experienceLevel', e.target.value)}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0e3a61] bg-slate-50/50 text-sm font-semibold text-slate-700"
                    >
                      <option value="">All Experience Levels</option>
                      <option value="entry">Entry Level</option>
                      <option value="mid">Mid Level</option>
                      <option value="senior">Senior Level</option>
                      <option value="executive">Executive</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">State</label>
                    <select
                      value={filters.state}
                      onChange={(e) => {
                        handleFilterChange('state', e.target.value);
                        handleFilterChange('city', ''); // Reset city on state change
                      }}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0e3a61] bg-slate-50/50 text-sm font-semibold text-slate-700"
                    >
                      <option value="">All States</option>
                      {Object.keys(locationsByState).sort().map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">City/District</label>
                    <select
                      value={filters.city}
                      onChange={(e) => handleFilterChange('city', e.target.value)}
                      disabled={!filters.state}
                      className={`w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0e3a61] bg-slate-50/50 text-sm font-semibold text-slate-700 ${!filters.state ? 'bg-slate-100 cursor-not-allowed opacity-60' : ''}`}
                    >
                      <option value="">All Cities</option>
                      {filters.state && locationsByState[filters.state]?.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">Gender</label>
                    <select
                      value={filters.gender}
                      onChange={(e) => handleFilterChange('gender', e.target.value)}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0e3a61] bg-slate-50/50 text-sm font-semibold text-slate-700"
                    >
                      <option value="">All Genders</option>
                      <option value="male">Male / पुरुष</option>
                      <option value="female">Female / स्त्री</option>
                    </select>
                  </div>
                </div>

                {/* Results Count and Clear Filters */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600">
                    <span>Aggregated Results: {filteredJobs.length} of {jobs.length} matches</span>
                    {filteredJobs.length !== jobs.length && (
                      <span className="px-2 py-0.5 bg-[#0e3a61]/10 text-[#0e3a61] rounded-md">
                        Filtered
                      </span>
                    )}
                  </div>
                  {(filters.search || filters.jobType || filters.workMode || filters.experienceLevel || filters.state || filters.city || filters.gender) && (
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-1.5 text-xs font-bold text-[#e2822f] hover:text-[#c97024] transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>Reset Filter Parameters</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Jobs List */}
            {filteredJobs.length === 0 ? (
              <div className="bg-white rounded-[2rem] shadow-xl p-12 text-center border border-slate-100">
                <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-slate-100 border-dashed">
                  <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-2">
                  {jobs.length === 0 ? 'Archive is vacant' : 'No records match search parameters'}
                </h3>
                <p className="text-xs text-slate-500 mb-6 max-w-sm mx-auto">
                  {jobs.length === 0
                    ? 'Check back later for new career pipelines. Strategic opportunities are added on a daily cycle.'
                    : 'Adjust your query strings or status filter settings to discover matching job profiles.'
                  }
                </p>
                {jobs.length > 0 && (
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center px-6 py-3 bg-[#0e3a61] hover:bg-[#0b2e4e] text-white font-bold text-xs rounded-xl shadow-md transition-colors"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Reset Filter Parameters</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredJobs.map((job) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    showActions={false}
                    user={user}
                    userData={userData}
                    onApplyClick={handleApplyClick}
                    onClick={() => {
                      navigate(`/jobs/${job.id}`);
                    }}
                  />
                ))}
              </div>
            )}

            {/* Load More */}
            {filteredJobs.length > 0 && filteredJobs.length === jobs.length && jobs.length >= 100 && (
              <div className="text-center mt-8">
                <button
                  onClick={() => {
                    console.log('Load more jobs');
                  }}
                  className="inline-flex items-center px-6 py-3 bg-[#0e3a61] hover:bg-[#0b2e4e] text-white font-bold text-xs rounded-xl shadow-md transition-all duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Expose Additional Records</span>
                </button>
              </div>
            )}
          </div>
        </ContentWithAds>
      </div>

      {/* Success Animation */}
      {showSuccessAnimation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl transform transition-all duration-300 scale-100">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">🎉 Application Submitted!</h3>
            <p className="text-gray-600 mb-4">Your application has been sent successfully. Good luck!</p>
            <div className="flex items-center justify-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Job Application Modal */}
      <JobApplicationModal
        job={selectedJob}
        isOpen={showJobApplicationModal}
        onClose={() => {
          setShowJobApplicationModal(false);
          setSelectedJob(null);
        }}
        onConfirm={handleConfirmApplication}
        applying={applying}
      />

      {/* Footer */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </>
  );
};

export default Jobs;
